import { memo, useState } from "react";
import type { MapFeatureValue } from "@/features/explorer/api/contracts";
import type { MetricDefinition } from "@/features/explorer/types";
import { computeStatistics, formatMetricValue, formatZScore } from "../lib/map-utils";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { CollapsibleRegion } from "@/components/ui/collapsible-region";
import { X, ArrowRightLeft, HelpCircle, BarChart2, ChevronDown, ChevronUp } from "lucide-react";

type RegionDetail = {
  geographyCode: string;
  name: string;
  value: number | null;
  qualityFlag?: string | null;
};

type ExplorerComparisonFlyoutProps = {
  isOpen: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onClose: () => void;
  onSwapRegions?: () => void;
  primaryRegion: RegionDetail | null;
  secondaryRegion: RegionDetail | null;
  allValues: MapFeatureValue[];
  metric: MetricDefinition;
  /**
   * Surface treatment. `floating` (default) renders the glass panel used on
   * desktop; `sheet` renders borderless/blur-free for embedding in the mobile
   * bottom sheet, which already provides an opaque surface — a nested
   * backdrop-blur over the WebGL canvas flickers on mobile GPUs during the
   * slide-up animation.
   */
  variant?: "floating" | "sheet";
};

export const ExplorerComparisonFlyout = memo(
  ({
    isOpen,
    isExpanded,
    onToggleExpand,
    onClose,
    onSwapRegions,
    primaryRegion,
    secondaryRegion,
    allValues,
    metric,
    variant = "floating",
  }: ExplorerComparisonFlyoutProps) => {
    const [showZScoreHelp, setShowZScoreHelp] = useState(false);

    if (!isOpen || !primaryRegion) return null;

    const validNumbers = allValues
      .map((v) => v.value)
      .filter((v): v is number => typeof v === "number" && Number.isFinite(v));

    const stats = computeStatistics(validNumbers);

    const valA = primaryRegion.value;
    const valB = secondaryRegion?.value ?? null;

    const diffAbs = valA !== null && valB !== null ? valA - valB : null;
    const diffPct = valA !== null && valB !== null && valB !== 0 ? ((valA - valB) / valB) * 100 : null;

    const valADeltaMean = valA !== null && stats ? valA - stats.mean : null;
    const valBDeltaMean = valB !== null && stats ? valB - stats.mean : null;

    return (
      <div
        className={cn(
          "w-full text-card-foreground",
          variant === "sheet"
            ? "bg-transparent"
            : "rounded-lg border border-border bg-card/95 backdrop-blur-2xl transition-shadow duration-300",
          variant === "floating" && (isExpanded ? "shadow-2xl" : "shadow-lg"),
        )}>
        {isExpanded ? (
          variant === "sheet" ? null : (
            <div className='mb-3 flex items-center justify-between border-b border-border px-4 pt-4 pb-3'>
              <div className='flex items-center gap-2'>
                {secondaryRegion && onSwapRegions ? (
                  <button
                    type='button'
                    onClick={onSwapRegions}
                    className='group relative cursor-pointer rounded p-1 text-primary transition-colors hover:bg-primary/20 hover:text-primary'
                    title='Swap A/B regions'
                    aria-label='Swap A/B regions'>
                    <ArrowRightLeft className='size-4 transition-transform group-hover:rotate-180' />
                  </button>
                ) : (
                  <ArrowRightLeft className='size-4 text-primary shrink-0' />
                )}
                <h2 className='text-sm font-bold tracking-wider text-card-foreground uppercase'>Regional Comparison</h2>
              </div>
              <div className='flex items-center gap-1'>
                <button
                  type='button'
                  onClick={onToggleExpand}
                  className='cursor-pointer rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-card-foreground'
                  title='Minimize panel'
                  aria-label='Minimize panel'
                  aria-expanded
                  aria-controls='comparison-flyout-body'>
                  <ChevronUp className='size-4' />
                </button>
                <button
                  type='button'
                  onClick={onClose}
                  className='cursor-pointer rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-card-foreground'
                  title='Close comparison'>
                  <X className='size-4' />
                </button>
              </div>
            </div>
          )
        ) : (
          <button
            type='button'
            onClick={onToggleExpand}
            aria-expanded={false}
            aria-controls='comparison-flyout-body'
            className='flex w-full cursor-pointer items-center justify-between rounded-lg px-4 py-3 text-base font-bold text-card-foreground transition-colors hover:bg-muted'>
            <div className='flex items-center gap-2.5 text-left min-w-0 flex-1 pr-2'>
              <ArrowRightLeft className='size-4 shrink-0 text-primary' />
              <span className='truncate text-base font-bold'>
                Compare ({primaryRegion.name}
                {secondaryRegion ? ` vs ${secondaryRegion.name}` : ""})
              </span>
            </div>
            <ChevronDown className='size-4 shrink-0 text-muted-foreground' />
          </button>
        )}

        <CollapsibleRegion open={isExpanded} id='comparison-flyout-body'>
          <div className={variant === "sheet" ? "" : "max-h-[calc(100vh-12rem)] overflow-y-auto px-4 pb-4"}>
            <div className='space-y-4 text-sm'>
              {/* Side-by-Side Comparison Cards */}
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1.5'>
                {/* Region A (Primary) */}
                <div className='relative rounded-md border border-primary/40 bg-primary/10 px-3.5 pt-5 pb-3.5 flex flex-col justify-between min-h-26'>
                  <Badge className='absolute -top-2.5 left-2.5 rounded-xs bg-primary px-2 py-0.5 text-xs font-bold text-primary-foreground uppercase shadow-xs'>
                    Primary (A)
                  </Badge>
                  <div className='pt-1'>
                    <h4 className='text-sm font-bold text-card-foreground leading-snug wrap-break-word'>
                      {primaryRegion.name}
                    </h4>
                  </div>
                  <div className='mt-4 pt-3 border-t border-primary/20 flex items-baseline justify-between gap-1'>
                    <span className='text-base font-extrabold tracking-tight text-primary font-mono'>
                      {formatMetricValue(valA, metric.unit)}
                    </span>
                  </div>
                </div>

                {/* Region B (Secondary) */}
                <div className='relative rounded-md border border-secondary/60 bg-secondary/30 px-3.5 pt-5 pb-3.5 flex flex-col justify-between min-h-26'>
                  <Badge className='absolute -top-2.5 left-2.5 rounded-xs bg-secondary px-2 py-0.5 text-xs font-bold text-secondary-foreground uppercase shadow-xs'>
                    Benchmark (B)
                  </Badge>
                  {secondaryRegion ? (
                    <>
                      <div className='pt-1'>
                        <h4 className='text-sm font-bold text-card-foreground leading-snug wrap-break-word'>
                          {secondaryRegion.name}
                        </h4>
                      </div>
                      <div className='mt-4 pt-3 border-t border-border flex items-baseline justify-between gap-1'>
                        <span className='text-base font-extrabold tracking-tight text-foreground font-mono'>
                          {formatMetricValue(valB, metric.unit)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className='flex h-full min-h-20 flex-col items-center justify-center text-center text-sm text-muted-foreground italic pt-2'>
                      <span>Click 2nd region on map to compare</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Differential Variance Card */}
              {diffAbs !== null && diffPct !== null && (
                <div className='space-y-2.5 rounded-md border border-border bg-muted/40 p-3.5 text-sm'>
                  {variant === "sheet" && secondaryRegion && onSwapRegions ? (
                    <div className='flex items-center justify-between gap-2 pb-0.5'>
                      <span className='text-xs font-semibold tracking-wider text-muted-foreground uppercase'>
                        Variance (A vs B)
                      </span>
                      <button
                        type='button'
                        onClick={onSwapRegions}
                        className='cursor-pointer rounded p-1 text-primary transition-colors hover:bg-primary/20'
                        title='Swap A/B regions'
                        aria-label='Swap A/B regions'>
                        <ArrowRightLeft className='size-3.5' />
                      </button>
                    </div>
                  ) : (
                    <span className='block text-xs font-semibold tracking-wider text-muted-foreground uppercase pb-0.5'>
                      Variance (A vs B)
                    </span>
                  )}
                  <div className='flex items-center justify-between gap-2 pt-1'>
                    <span className='text-xs font-medium text-muted-foreground'>Absolute Difference (Δ):</span>
                    <span
                      className={`font-mono text-sm font-bold ${diffAbs >= 0 ? "text-success" : "text-destructive"}`}>
                      {diffAbs >= 0 ? "+" : ""}
                      {formatMetricValue(diffAbs, metric.unit)}
                    </span>
                  </div>
                  <div className='flex items-center justify-between border-t border-border/80 pt-2 text-xs gap-2'>
                    <span className='text-muted-foreground'>Percentage Difference:</span>
                    <span
                      className={`font-mono text-sm font-bold ${diffPct >= 0 ? "text-success" : "text-destructive"}`}>
                      {diffPct >= 0 ? "+" : ""}
                      {diffPct.toFixed(2)}%
                    </span>
                  </div>
                </div>
              )}

              {/* National Average Delta Section */}
              <div className='space-y-3 rounded-md border border-border bg-muted/40 p-3.5 text-sm'>
                {/* Header Row */}
                <div className='flex items-center justify-between gap-2 pb-0.5'>
                  <span className='text-xs font-semibold tracking-wider text-muted-foreground uppercase'>
                    National Mean Variance
                  </span>
                  <button
                    type='button'
                    onClick={() => {
                      setShowZScoreHelp(!showZScoreHelp);
                    }}
                    className='cursor-pointer text-muted-foreground transition-colors hover:text-primary'
                    title='Toggle explanation'>
                    <HelpCircle className='size-3.5' />
                  </button>
                </div>

                {/* Dedicated UK Mean Row */}
                {stats && (
                  <div className='flex items-center justify-between rounded border border-border bg-card px-3 py-2 font-mono text-xs'>
                    <span className='font-sans font-medium text-muted-foreground'>UK Benchmark Mean:</span>
                    <span className='font-bold text-card-foreground'>{formatMetricValue(stats.mean, metric.unit)}</span>
                  </div>
                )}

                {showZScoreHelp && (
                  <div className='rounded border border-border bg-muted p-2.5 text-xs/relaxed text-muted-foreground transition duration-150 animate-in fade-in-0'>
                    💡 <strong>National Mean Variance:</strong> Measures variance from the UK national average (
                    {stats ? formatMetricValue(stats.mean, metric.unit) : "N/A"}) and standard deviation (
                    {stats ? formatMetricValue(stats.stdDev, metric.unit) : "N/A"}).
                  </div>
                )}

                {stats && valADeltaMean !== null && (
                  <div className='space-y-2.5 text-xs pt-1'>
                    {/* Region A */}
                    <div className='rounded border border-border bg-card p-3 space-y-2'>
                      <div className='flex items-center justify-between gap-2'>
                        <span className='font-bold text-card-foreground leading-tight truncate'>
                          {primaryRegion.name} (A)
                        </span>
                        <span className='font-mono text-xs text-muted-foreground font-semibold shrink-0'>
                          Z: {formatZScore(valA, stats.mean, stats.stdDev)}
                        </span>
                      </div>
                      <div className='flex items-center justify-between pt-2 border-t border-border/60 gap-2'>
                        <span className='text-muted-foreground text-xs'>vs UK Mean:</span>
                        <span
                          className={`font-mono text-xs font-bold ${valADeltaMean >= 0 ? "text-success" : "text-destructive"}`}>
                          {valADeltaMean >= 0 ? "+" : ""}
                          {formatMetricValue(valADeltaMean, metric.unit)}
                        </span>
                      </div>
                    </div>

                    {/* Region B */}
                    {secondaryRegion && valBDeltaMean !== null && (
                      <div className='rounded border border-border bg-card p-3 space-y-2'>
                        <div className='flex items-center justify-between gap-2'>
                          <span className='font-bold text-card-foreground leading-tight truncate'>
                            {secondaryRegion.name} (B)
                          </span>
                          <span className='font-mono text-xs text-muted-foreground font-semibold shrink-0'>
                            Z: {formatZScore(valB, stats.mean, stats.stdDev)}
                          </span>
                        </div>
                        <div className='flex items-center justify-between pt-2 border-t border-border/60 gap-2'>
                          <span className='text-muted-foreground text-xs'>vs UK Mean:</span>
                          <span
                            className={`font-mono text-xs font-bold ${valBDeltaMean >= 0 ? "text-success" : "text-destructive"}`}>
                            {valBDeltaMean >= 0 ? "+" : ""}
                            {formatMetricValue(valBDeltaMean, metric.unit)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Distribution Benchmark */}
              {stats && (
                <div className='space-y-2.5 rounded-md border border-border bg-muted/40 p-3.5 text-sm'>
                  <div className='flex items-center gap-1.5 text-xs font-semibold tracking-wider text-muted-foreground uppercase pb-0.5'>
                    <BarChart2 className='size-3.5 text-primary' />
                    <span>Distribution Benchmark</span>
                  </div>

                  <div className='grid grid-cols-2 gap-2 font-mono text-xs pt-1'>
                    <div className='rounded border border-border bg-card p-2.5'>
                      <span className='block font-sans text-xs text-muted-foreground font-medium'>Min Value</span>
                      <span className='block font-bold text-card-foreground mt-0.5'>
                        {formatMetricValue(stats.min, metric.unit)}
                      </span>
                    </div>
                    <div className='rounded border border-border bg-card p-2.5'>
                      <span className='block font-sans text-xs text-muted-foreground font-medium'>Max Value</span>
                      <span className='block font-bold text-card-foreground mt-0.5'>
                        {formatMetricValue(stats.max, metric.unit)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CollapsibleRegion>
      </div>
    );
  },
);

ExplorerComparisonFlyout.displayName = "ExplorerComparisonFlyout";
